import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid2 from '@mui/material/Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';
import { Email } from '@mui/icons-material';
import logo from './../../../assets/images/logo.png';
import { forgotPassword } from '../store/auth.actions';
import { forgotError, forgotLoading } from '../store/auth.selectors';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  BoxContainer,
  AuthTextField,
  PrimaryButton,
  LogoContainer,
  StyledLogo,
  FormContainer,
} from '../../../utils/common/styles/commonStyles';

// Styled Components
const PageContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  padding: theme.spacing(2, 3),
}));

const ForgotPasswordPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector(forgotLoading);
  const error = useSelector(forgotError);

  const [showAlert, setShowAlert] = useState({
    open: false,
    message: '',
    severity: '',
  });

  const validationSchema = Yup.object({
    email: Yup.string().email('Invalid Email').required('Email is required'),
  });

  const handleSignInRedirect = () => {
    navigate('/login');
  };

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      const result = await dispatch(forgotPassword(values.email));

      if (result) {
        setShowAlert({
          open: true,
          message: 'If the email is registered, a reset link has been sent.',
          severity: 'success',
        });
      } else {
        setShowAlert({
          open: true,
          message: error,
          severity: 'error',
        });
      }

      setTimeout(() => {
        if (result) handleSignInRedirect();
      }, 3000);
    },
  });

  const CustomEmailInputProps = {
    startAdornment: (
      <InputAdornment position="start">
        <Email color="action" />
      </InputAdornment>
    ),
  };

  const handleAlertClose = (reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowAlert((prev) => ({ ...prev, open: false }));
  };

  return (
    <PageContainer maxWidth="lg">
      <Grid2 container justifyContent="center">
        <Grid2 item xs={12} sm={8} md={6} lg={5}>
          <BoxContainer>
            <LogoContainer textAlign="center">
              <StyledLogo src={logo} alt="Rieker Logo" />
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{
                  color: '#2c3e50',
                  marginTop: 1,
                  background: 'linear-gradient(to right, #283048, #859398)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                SIGNS
              </Typography>
            </LogoContainer>

            <Typography
              variant="h5"
              fontWeight="600"
              textAlign="center"
              sx={{
                color: '#2c3e50',
                marginBottom: 3,
              }}
            >
              Reset Password
            </Typography>

            <FormContainer onSubmit={formik.handleSubmit}>
              <AuthTextField
                fullWidth
                label="Email Address"
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                margin="normal"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                slotProps={{ input: CustomEmailInputProps }}
              />

              {showAlert.open && (
                <Alert
                  severity={showAlert.severity}
                  sx={{
                    marginTop: 2,
                    borderRadius: 2,
                    '& .MuiAlert-message': {
                      fontWeight: 500,
                    },
                  }}
                  onClose={handleAlertClose}
                >
                  {showAlert.message}
                </Alert>
              )}

              <Box mt={4} gap={3} display="flex" justifyContent="center" alignItems="center">
                <PrimaryButton type="button" variant="outlined" onClick={handleSignInRedirect}>
                  Cancel
                </PrimaryButton>
                <PrimaryButton
                  type="submit"
                  variant="contained"
                  disabled={loading || !formik.isValid}
                  sx={{ minWidth: 160 }}
                >
                  {loading ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={20} color="inherit" />
                      <span>Sending Email...</span>
                    </Box>
                  ) : (
                    'Request'
                  )}
                </PrimaryButton>
              </Box>
            </FormContainer>
          </BoxContainer>
        </Grid2>
      </Grid2>
    </PageContainer>
  );
};

export default ForgotPasswordPage;
